/**
 * マスターデータ管理サービス（実API実装）
 *
 * バックエンドAPI呼び出し:
 * - GET /api/admin/master/risk-categories - リスクカテゴリ一覧取得
 * - GET /api/admin/master/ma-methods - M&A手法一覧取得
 * - GET /api/admin/master/countries - 対象国一覧取得
 * - PUT /api/admin/master/:type/:id - マスターデータ更新
 * - POST /api/admin/master/:type - マスターデータ作成
 * - DELETE /api/admin/master/:type/:id - マスターデータ削除
 */
import apiClient, { extractErrorMessage } from '../../lib/apiClient';

// ===== 型定義 =====

/**
 * リスクカテゴリデータ
 */
export interface RiskCategoryData {
  id: string;
  code: string;
  name: string;
  description: string;
  itemCount: number;
  order: number;
  isActive: boolean;
}

/**
 * M&A手法データ
 */
export interface MAMethodData {
  id: string;
  code: string;
  name: string;
  description: string;
  order: number;
  isActive: boolean;
}

/**
 * 対象国データ
 */
export interface CountryData {
  id: string;
  code: string;
  name: string;
  apiSource: string;
  lawCount: number;
  isActive: boolean;
}

/**
 * マスターデータ作成リクエスト
 */
export interface CreateMasterDataRequest {
  code: string;
  name: string;
  description?: string;
  apiSource?: string;
}

/**
 * マスターデータ更新リクエスト
 */
export interface UpdateMasterDataRequest {
  code?: string;
  name?: string;
  description?: string;
  apiSource?: string;
  isActive?: boolean;
  order?: number;
}

// ===== バックエンドレスポンス型（snake_case） =====

interface BackendRiskCategoryData {
  id: string;
  code: string;
  name: string;
  description: string;
  item_count: number;
  display_order: number;
  is_active: boolean;
}

interface BackendMAMethodData {
  id: string;
  code: string;
  name: string;
  description: string;
  display_order: number;
  is_active: boolean;
}

interface BackendCountryData {
  id: string;
  code: string;
  name: string;
  api_source: string;
  law_count: number;
  is_active: boolean;
}

// ===== 変換関数（snake_case -> camelCase） =====

function convertRiskCategory(data: BackendRiskCategoryData): RiskCategoryData {
  return {
    id: data.id,
    code: data.code,
    name: data.name,
    description: data.description,
    itemCount: data.item_count,
    order: data.display_order,
    isActive: data.is_active,
  };
}

function convertMAMethod(data: BackendMAMethodData): MAMethodData {
  return {
    id: data.id,
    code: data.code,
    name: data.name,
    description: data.description,
    order: data.display_order,
    isActive: data.is_active,
  };
}

function convertCountry(data: BackendCountryData): CountryData {
  return {
    id: data.id,
    code: data.code,
    name: data.name,
    apiSource: data.api_source,
    lawCount: data.law_count,
    isActive: data.is_active,
  };
}

// ===== マスターデータサービス =====

export const masterDataService = {
  // ===== リスクカテゴリ =====

  /**
   * リスクカテゴリ一覧取得
   * GET /api/admin/master/risk-categories
   */
  async getRiskCategories(): Promise<RiskCategoryData[]> {
    try {
      const response = await apiClient.get<{ data: BackendRiskCategoryData[] }>(
        '/api/admin/master/risk-categories'
      );
      return response.data.data.map(convertRiskCategory);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  /**
   * リスクカテゴリ作成
   * POST /api/admin/master/risk-categories
   */
  async createRiskCategory(data: CreateMasterDataRequest): Promise<RiskCategoryData> {
    try {
      const response = await apiClient.post<BackendRiskCategoryData>(
        '/api/admin/master/risk-categories',
        {
          code: data.code,
          name: data.name,
          description: data.description || '',
        }
      );
      return convertRiskCategory(response.data);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  /**
   * リスクカテゴリ更新
   * PUT /api/admin/master/risk-categories/:id
   */
  async updateRiskCategory(id: string, data: UpdateMasterDataRequest): Promise<RiskCategoryData> {
    try {
      const requestData: Record<string, unknown> = {};
      if (data.code !== undefined) requestData.code = data.code;
      if (data.name !== undefined) requestData.name = data.name;
      if (data.description !== undefined) requestData.description = data.description;
      if (data.isActive !== undefined) requestData.is_active = data.isActive;
      if (data.order !== undefined) requestData.display_order = data.order;

      const response = await apiClient.put<BackendRiskCategoryData>(
        `/api/admin/master/risk-categories/${id}`,
        requestData
      );
      return convertRiskCategory(response.data);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  /**
   * リスクカテゴリ削除
   * DELETE /api/admin/master/risk-categories/:id
   */
  async deleteRiskCategory(id: string): Promise<void> {
    try {
      await apiClient.delete(`/api/admin/master/risk-categories/${id}`);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  // ===== M&A手法 =====

  /**
   * M&A手法一覧取得
   * GET /api/admin/master/ma-methods
   */
  async getMAMethods(): Promise<MAMethodData[]> {
    try {
      const response = await apiClient.get<{ data: BackendMAMethodData[] }>(
        '/api/admin/master/ma-methods'
      );
      return response.data.data.map(convertMAMethod);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  /**
   * M&A手法作成
   * POST /api/admin/master/ma-methods
   */
  async createMAMethod(data: CreateMasterDataRequest): Promise<MAMethodData> {
    try {
      const response = await apiClient.post<BackendMAMethodData>(
        '/api/admin/master/ma-methods',
        {
          code: data.code,
          name: data.name,
          description: data.description || '',
        }
      );
      return convertMAMethod(response.data);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  /**
   * M&A手法更新
   * PUT /api/admin/master/ma-methods/:id
   */
  async updateMAMethod(id: string, data: UpdateMasterDataRequest): Promise<MAMethodData> {
    try {
      const requestData: Record<string, unknown> = {};
      if (data.code !== undefined) requestData.code = data.code;
      if (data.name !== undefined) requestData.name = data.name;
      if (data.description !== undefined) requestData.description = data.description;
      if (data.isActive !== undefined) requestData.is_active = data.isActive;
      if (data.order !== undefined) requestData.display_order = data.order;

      const response = await apiClient.put<BackendMAMethodData>(
        `/api/admin/master/ma-methods/${id}`,
        requestData
      );
      return convertMAMethod(response.data);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  /**
   * M&A手法削除
   * DELETE /api/admin/master/ma-methods/:id
   */
  async deleteMAMethod(id: string): Promise<void> {
    try {
      await apiClient.delete(`/api/admin/master/ma-methods/${id}`);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  // ===== 対象国 =====

  /**
   * 対象国一覧取得
   * GET /api/admin/master/countries
   */
  async getCountries(): Promise<CountryData[]> {
    try {
      const response = await apiClient.get<{ data: BackendCountryData[] }>(
        '/api/admin/master/countries'
      );
      return response.data.data.map(convertCountry);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  /**
   * 対象国作成
   * POST /api/admin/master/countries
   */
  async createCountry(data: CreateMasterDataRequest): Promise<CountryData> {
    try {
      const response = await apiClient.post<BackendCountryData>(
        '/api/admin/master/countries',
        {
          code: data.code,
          name: data.name,
          api_source: data.apiSource || '',
        }
      );
      return convertCountry(response.data);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  /**
   * 対象国更新
   * PUT /api/admin/master/countries/:id
   */
  async updateCountry(id: string, data: UpdateMasterDataRequest): Promise<CountryData> {
    try {
      const requestData: Record<string, unknown> = {};
      if (data.code !== undefined) requestData.code = data.code;
      if (data.name !== undefined) requestData.name = data.name;
      if (data.apiSource !== undefined) requestData.api_source = data.apiSource;
      if (data.isActive !== undefined) requestData.is_active = data.isActive;
      if (data.order !== undefined) requestData.display_order = data.order;

      const response = await apiClient.put<BackendCountryData>(
        `/api/admin/master/countries/${id}`,
        requestData
      );
      return convertCountry(response.data);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  /**
   * 対象国削除
   * DELETE /api/admin/master/countries/:id
   */
  async deleteCountry(id: string): Promise<void> {
    try {
      await apiClient.delete(`/api/admin/master/countries/${id}`);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },
};

export default masterDataService;
